// components/invoices/InvoiceForm.tsx - PROPERLY FIXED

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Autocomplete,
  InputAdornment,
  CircularProgress,
  Checkbox,
  Tooltip,
  Badge,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Send as SendIcon,
  Save as SaveIcon,
  AttachFile as AttachFileIcon,
  Calculate as CalculateIcon,
  CheckCircle as CheckIcon,
  ContentCopy as CopyIcon,
  Lightbulb as SuggestionIcon,
  Preview as PreviewIcon,
  Clear as ClearIcon,
  DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import axiosInstance from '../../utils/axiosConfig';
import { getDropdownOptions, DropdownOption } from '../../constants/dropdownOptions';
import { getIMCCodeOptions } from '../../constants/dropdownOptions';
import ShowRunnrCalc from '../ShowRunnrCalc';
import { useAuth } from '../../contexts/AuthContext';
import { SubmissionProgressModal } from './SubmissionProgressModal';


interface Personnel {
  _id: string;
  name: string;
  email: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  phone?: string;
}

interface StagedFile {
  file: File;
  preview?: string;
  uploading?: boolean;
  uploaded?: boolean;
  error?: string;
}

interface LineItem {
  itemName: string;
  description: string;
  quantity: number;
  unitPrice: number;
  companyPercentage: number;
  totalAmount: number;
  mondayEventId: string;
  activityName: string;
  imcCode: string;
  imcCodeDescription: string;
  imcCodeMondayId?: string;
  stagedProof?: StagedFile;
  proofAttachment?: {
    filename: string;
    url: string;
    uploadedAt: Date;
  };
  selected?: boolean;
}

interface InvoiceFormData {
  lineItems: LineItem[];
  notes: string;
  vatAmount: number;
  invoiceDate: Dayjs;
  dueDate: Dayjs;
}

interface InvoiceFormProps {
  invoiceId?: string;
  onSubmit?: (invoice: any) => void;
  onCancel?: () => void;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({
  invoiceId: initialInvoiceId,
  onSubmit,
  onCancel
}) => {
  const [invoiceId, setInvoiceId] = useState<string | undefined>(initialInvoiceId);
  const [formData, setFormData] = useState<InvoiceFormData>({
    lineItems: [createEmptyLineItem()],
    notes: '',
    vatAmount: 0,
    invoiceDate: dayjs(),
    dueDate: dayjs().add(10, 'day')
  });

  const [currentUser, setCurrentUser] = useState<Personnel | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  
  const [mondayEvents, setMondayEvents] = useState<DropdownOption[]>([]);
  const [imcCodes, setImcCodes] = useState<DropdownOption[]>([]);
  
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | 'info' | null;
    message: string | null;
  }>({ type: null, message: null });
  
  const [submitting, setSubmitting] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  
  const [uploadProgress, setUploadProgress] = useState<{
    show: boolean;
    current: number;
    total: number;
    currentFile: string;
  }>({ show: false, current: 0, total: 0, currentFile: '' });
  
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [calculatorTarget, setCalculatorTarget] = useState<{
    lineIndex: number;
    field: 'unitPrice' | 'quantity';
  } | null>(null);

  const [submissionProgress, setSubmissionProgress] = useState<{
  open: boolean;
  steps: Array<{
    label: string;
    status: 'pending' | 'loading' | 'complete' | 'error';
    detail?: string;
  }>;
  currentStep: number;
  error: string | null;
}>({
  open: false,
  steps: [],
  currentStep: 0,
  error: null
});

  function createEmptyLineItem(): LineItem {
    return {
      itemName: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      companyPercentage: 100,
      totalAmount: 0,
      mondayEventId: '',
      activityName: '',
      imcCode: '',
      imcCodeDescription: '',
      selected: false
    };
  }

  useEffect(() => {
    const initializeForm = async () => {
      try {
        setLoading(true);
        
        const userResponse = await axiosInstance.get('/api/users/me');
        const personnelResponse = await axiosInstance.get('/api/personnel/search', {
          params: { email: userResponse.data.email }
        });
        
        if (personnelResponse.data && personnelResponse.data.length > 0) {
          setCurrentUser(personnelResponse.data[0]);
        } else {
          setError('Personnel record not found. Please contact admin.');
          return;
        }
        
        const [mondayOptions, imcOptions] = await Promise.all([
          getDropdownOptions(),
          getIMCCodeOptions()
        ]);
        
        setMondayEvents(mondayOptions);
        setImcCodes(imcOptions);
        
        if (invoiceId) {
          const invoiceResponse = await axiosInstance.get(`/api/invoices/${invoiceId}`);
          const invoice = invoiceResponse.data;
          
          setFormData({
            lineItems: invoice.lineItems,
            notes: invoice.notes || '',
            vatAmount: invoice.vatAmount || 0,
            invoiceDate: dayjs(invoice.invoiceDate),
            dueDate: invoice.dueDate
              ? dayjs(invoice.dueDate)
              : dayjs(invoice.invoiceDate).add(10, 'day')
          });
        }
        
      } catch (err: any) {
        console.error('Error initializing form:', err);
        setError(err.response?.data?.message || 'Failed to load form data');
      } finally {
        setLoading(false);
      }
    };

    initializeForm();
  }, [invoiceId]);

  const calculateLineItemTotal = (item: LineItem): number => {
    return item.quantity * item.unitPrice * (item.companyPercentage / 100);
  };

  const calculateSubtotal = (): number => {
    return formData.lineItems.reduce((sum, item) => sum + calculateLineItemTotal(item), 0);
  };

  const calculateTotal = (): number => {
    return calculateSubtotal() + formData.vatAmount;
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: any) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };
          
          if (field === 'quantity' || field === 'unitPrice' || field === 'companyPercentage') {
            updatedItem.totalAmount = calculateLineItemTotal(updatedItem);
          }

          return updatedItem;
        }
        return item;
      })
    }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, createEmptyLineItem()]
    }));
  };

  const duplicateLineItem = (index: number) => {
    const item = formData.lineItems[index];
    const duplicated = { 
      ...item, 
      selected: false,
      stagedProof: undefined,
      proofAttachment: undefined
    };
    
    setFormData(prev => ({
      ...prev,
      lineItems: [
        ...prev.lineItems.slice(0, index + 1),
        duplicated,
        ...prev.lineItems.slice(index + 1)
      ]
    }));
  };

  const removeLineItem = (index: number) => {
    if (formData.lineItems.length > 1) {
      setFormData(prev => ({
        ...prev,
        lineItems: prev.lineItems.filter((_, i) => i !== index)
      }));
    }
  };

  const clearLineItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) => 
        i === index ? createEmptyLineItem() : item
      )
    }));
  };

  const setCompanyPercentage = (index: number, percentage: number) => {
    updateLineItem(index, 'companyPercentage', percentage);
  };

  const toggleSelectAll = () => {
    const allSelected = formData.lineItems.every(item => item.selected);
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => ({ ...item, selected: !allSelected }))
    }));
  };

  const deleteSelected = () => {
    const remaining = formData.lineItems.filter(item => !item.selected);
    if (remaining.length === 0) {
      setSubmitStatus({
        type: 'error',
        message: 'Cannot delete all line items'
      });
      return;
    }
    setFormData(prev => ({ ...prev, lineItems: remaining }));
  };

  const stageFile = async (index: number, file: File) => {
    try {
      let preview: string | undefined;
      if (file.type.startsWith('image/')) {
        preview = URL.createObjectURL(file);
      }
      
      const stagedFile: StagedFile = {
        file,
        preview,
        uploading: false,
        uploaded: false
      };
      
      updateLineItem(index, 'stagedProof', stagedFile);
      updateLineItem(index, 'proofAttachment', undefined);
      
      setSubmitStatus({
        type: 'success',
        message: `Proof ${index + 1} staged successfully.`
      });
      
    } catch (err: any) {
      console.error('Error staging file:', err);
      setSubmitStatus({
        type: 'error',
        message: `Failed to stage proof ${index + 1}: ${err.message}`
      });
    }
  };

  const clearStagedFile = (index: number) => {
    const item = formData.lineItems[index];
    if (item.stagedProof?.preview) {
      URL.revokeObjectURL(item.stagedProof.preview);
    }
    updateLineItem(index, 'stagedProof', undefined);
  };

  const uploadAllStagedFiles = async (targetInvoiceId: string): Promise<boolean> => {
    const stagedFiles = formData.lineItems
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.stagedProof);

    if (stagedFiles.length === 0) {
      return true;
    }

    setUploadProgress({
      show: true,
      current: 0,
      total: stagedFiles.length,
      currentFile: ''
    });

    try {
      for (let i = 0; i < stagedFiles.length; i++) {
        const { item, index } = stagedFiles[i];
        const stagedFile = item.stagedProof!;
        
        setUploadProgress(prev => ({
          ...prev,
          current: i + 1,
          currentFile: `Proof ${index + 1}: ${stagedFile.file.name}`
        }));

        updateLineItem(index, 'stagedProof', {
          ...stagedFile,
          uploading: true
        });

        const proofNumber = index + 1;
        const fileExtension = stagedFile.file.name.split('.').pop() || '';
        const sequentialFilename = `proof_${proofNumber}_${targetInvoiceId}.${fileExtension}`;

        const formDataUpload = new FormData();
        const renamedFile = new File([stagedFile.file], sequentialFilename, {
          type: stagedFile.file.type
        });
        
        formDataUpload.append('proof', renamedFile);
        
        const response = await axiosInstance.post(
          `/api/invoices/${targetInvoiceId}/upload-proof/${index}`,
          formDataUpload,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        updateLineItem(index, 'proofAttachment', {
          filename: response.data.filename,
          url: response.data.url,
          uploadedAt: new Date()
        });

        updateLineItem(index, 'stagedProof', {
          ...stagedFile,
          uploading: false,
          uploaded: true
        });
      }

      setUploadProgress({ show: false, current: 0, total: 0, currentFile: '' });
      
      setTimeout(() => {
        setFormData(prev => ({
          ...prev,
          lineItems: prev.lineItems.map(item => ({
            ...item,
            stagedProof: undefined
          }))
        }));
      }, 1000);

      return true;

    } catch (err: any) {
      console.error('Batch upload failed:', err);
      setUploadProgress({ show: false, current: 0, total: 0, currentFile: '' });
      setSubmitStatus({
        type: 'error',
        message: `Failed to upload proof documents: ${err.response?.data?.message || err.message}`
      });
      return false;
    }
  };

  const handleSaveDraft = async () => {
    try {
      setSubmitStatus({ type: null, message: null });
      
      const invoiceData = {
        lineItems: formData.lineItems.map(item => ({
          ...item,
          stagedProof: undefined,
          selected: undefined
        })),
        notes: formData.notes,
        vatAmount: formData.vatAmount
      };
      
      let response;
      let targetInvoiceId: string;
      
      if (invoiceId) {
        response = await axiosInstance.put(`/api/invoices/${invoiceId}`, invoiceData);
        targetInvoiceId = invoiceId;
      } else {
        response = await axiosInstance.post('/api/invoices', invoiceData);
        targetInvoiceId = response.data._id;
        setInvoiceId(targetInvoiceId);
      }
      
      const uploadSuccess = await uploadAllStagedFiles(targetInvoiceId);
      
      if (uploadSuccess) {
        setSubmitStatus({
          type: 'success',
          message: 'Invoice saved as draft successfully'
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: 'Invoice saved but some proof documents failed to upload'
        });
      }
      
      if (onSubmit) {
        onSubmit(response.data);
      }
      
    } catch (err: any) {
      console.error('Error saving draft:', err);
      setSubmitStatus({
        type: 'error',
        message: err.response?.data?.message || 'Failed to save invoice'
      });
    }
  };

const handleSubmitInvoice = async () => {
  try {
    // Validate all required fields
    for (let i = 0; i < formData.lineItems.length; i++) {
      const item = formData.lineItems[i];
      if (!item.itemName || !item.description || !item.mondayEventId || !item.imcCode) {
        setSubmitStatus({
          type: 'error',
          message: `Line item ${i + 1} is missing required fields`
        });
        return;
      }
      if (item.quantity <= 0 || item.unitPrice < 0) {
        setSubmitStatus({
          type: 'error',
          message: `Line item ${i + 1} has invalid quantity or price`
        });
        return;
      }
    }
    
    // Open progress modal
    setSubmitting(true);
    setSubmissionProgress({
      open: true,
      steps: [
        { label: 'Saving invoice', status: 'loading' },
        { label: 'Uploading proof documents', status: 'pending' },
        { label: 'Generating PDF', status: 'pending' },
        { label: 'Sending to Monday.com', status: 'pending' }
      ],
      currentStep: 0,
      error: null
    });
    
    // Step 1: Save as draft
    let currentInvoiceId = invoiceId;
    if (!currentInvoiceId) {
      const draftResponse = await axiosInstance.post('/api/invoices', {
        lineItems: formData.lineItems.map(item => ({
          ...item,
          stagedProof: undefined
        })),
        notes: formData.notes,
        vatAmount: formData.vatAmount
      });
      currentInvoiceId = draftResponse.data._id;
      setInvoiceId(currentInvoiceId);
    }
    
    setSubmissionProgress(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => 
        i === 0 ? { ...step, status: 'complete' } : step
      ),
      currentStep: 1
    }));
    
    // Step 2: Upload proofs
    if (!currentInvoiceId) {
      throw new Error('Invoice ID is missing');
    }
    
    const stagedFiles = formData.lineItems.filter(item => item.stagedProof);
    if (stagedFiles.length > 0) {
      setSubmissionProgress(prev => ({
        ...prev,
        steps: prev.steps.map((step, i) => 
          i === 1 ? { ...step, status: 'loading', detail: `${stagedFiles.length} file(s)` } : step
        )
      }));
      
      const uploadSuccess = await uploadAllStagedFiles(currentInvoiceId);
      
      if (!uploadSuccess) {
        throw new Error('Failed to upload proof documents');
      }
    }
    
    setSubmissionProgress(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => 
        i === 1 ? { ...step, status: 'complete' } : step
      ),
      currentStep: 2
    }));
    
    // Step 3 & 4: Submit
    setSubmissionProgress(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => 
        i === 2 || i === 3 ? { ...step, status: 'loading' } : step
      )
    }));
    
    await axiosInstance.post(`/api/invoices/${currentInvoiceId}/submit`);
    
    setSubmissionProgress(prev => ({
      ...prev,
      steps: prev.steps.map(step => ({ ...step, status: 'complete' })),
      currentStep: 4
    }));
    
    // Show all complete briefly, then close
 setSubmitting(false);

    
  } catch (err: any) {
    console.error('Error submitting invoice:', err);
    setSubmitting(false);
    setSubmissionProgress(prev => ({
      ...prev,
      error: err.response?.data?.message || err.message || 'Failed to submit invoice'
    }));
  }
};

  const handleCalculatorSubmit = (value: number) => {
    if (calculatorTarget) {
      updateLineItem(calculatorTarget.lineIndex, calculatorTarget.field, value);
    }
    setCalculatorOpen(false);
    setCalculatorTarget(null);
  };

  const openCalculator = (lineIndex: number, field: 'unitPrice' | 'quantity') => {
    setCalculatorTarget({ lineIndex, field });
    setCalculatorOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading invoice form...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const selectedCount = formData.lineItems.filter(item => item.selected).length;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', gap: 3, p: { xs: 1, sm: 2, md: 3 }, maxWidth: '100%', flexDirection: { xs: 'column', lg: 'row' }, pb: { xs: 12, lg: 3 } }}>
        {/* Main Form */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
            <Typography variant="h4" gutterBottom>
              {invoiceId ? 'Edit Invoice' : 'Create New Invoice'}
            </Typography>

            {uploadProgress.show && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  Uploading {uploadProgress.current}/{uploadProgress.total}: {uploadProgress.currentFile}
                </Typography>
              </Alert>
            )}

            {/* Header */}
            {currentUser && (
              <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>From:</Typography>
                      <Typography variant="body1"><strong>{currentUser.name}</strong></Typography>
                      <Typography variant="body2" color="text.secondary">{currentUser.email}</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Invoice Date"
                        value={formData.invoiceDate}
                        onChange={(date) => setFormData(prev => ({ ...prev, invoiceDate: date || dayjs() }))}
                        format="DD/MM/YYYY"
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Due Date"
                        value={formData.dueDate}
                        onChange={(date) => setFormData(prev => ({ ...prev, dueDate: date || dayjs().add(10, 'day') }))}
                        format="DD/MM/YYYY"
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {selectedCount > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2">{selectedCount} selected</Typography>
                  <Button size="small" startIcon={<DeleteSweepIcon />} onClick={deleteSelected} color="error">
                    Delete Selected
                  </Button>
                </Stack>
              </Alert>
            )}

            {/* Line Items */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Checkbox
                checked={formData.lineItems.every(item => item.selected)}
                indeterminate={selectedCount > 0 && selectedCount < formData.lineItems.length}
                onChange={toggleSelectAll}
              />
              <Typography variant="h6">Line Items</Typography>
            </Stack>

            {formData.lineItems.map((item, index) => (
              <Card key={index} sx={{ mb: 2, p: { xs: 1.5, md: 2 } }}>
                <Grid container spacing={2}>
                  {/* Header Row */}
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Checkbox
                        checked={item.selected || false}
                        onChange={(e) => updateLineItem(index, 'selected', e.target.checked)}
                      />
                      <Typography variant="h6" color="primary">Item #{index + 1}</Typography>
                      <Box sx={{ flex: 1 }} />
                      <Tooltip title="Clear all fields">
                        <IconButton size="small" onClick={() => clearLineItem(index)}>
                          <ClearIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton size="small" onClick={() => duplicateLineItem(index)}>
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => removeLineItem(index)}
                          disabled={formData.lineItems.length === 1}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Grid>

                  {/* Item Name & Description */}
                  <Grid item xs={12} md={6}>
  <TextField
  fullWidth
  label="Item Name *"
  value={item.itemName}
  onChange={(e) => updateLineItem(index, 'itemName', e.target.value)}
  autoComplete="off"
  sx={{
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: !item.itemName ? 'info.main' : undefined
      }
    }
  }}
/>
                  </Grid>
                  <Grid item xs={12} md={6}>
<TextField
  fullWidth
  label="Description *"
  value={item.description}
  onChange={(e) => updateLineItem(index, 'description', e.target.value)}
  autoComplete="off"
  sx={{
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: !item.description ? 'warning.main' : undefined
      }
    }
  }}
/>
                  </Grid>

                  {/* Event & IMC */}
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      fullWidth
                      options={mondayEvents}
                      getOptionLabel={(option) => option.label}
                      value={mondayEvents.find(opt => opt.value === item.mondayEventId) || null}
                      onChange={(_, newValue) => {
                        updateLineItem(index, 'mondayEventId', newValue?.value || '');
                        updateLineItem(index, 'activityName', newValue?.label || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Event *"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: !item.mondayEventId ? 'error.main' : undefined
                              }
                            }
                          }}
                        />
                      )}
                    />
                    {index > 0 && formData.lineItems[index - 1].mondayEventId && (
                      <Chip
                        size="small"
                        label={`Same: ${formData.lineItems[index - 1].activityName}`}
                        onClick={() => {
                          const prev = formData.lineItems[index - 1];
                          updateLineItem(index, 'mondayEventId', prev.mondayEventId);
                          updateLineItem(index, 'activityName', prev.activityName);
                        }}
                        color="primary"
                        variant="outlined"
                        sx={{ mt: 0.75, maxWidth: '100%', cursor: 'pointer' }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      fullWidth
                      options={imcCodes}
                      getOptionLabel={(option) => option.label}
                      value={imcCodes.find(opt => opt.value === item.imcCode) || null}
                      onChange={(_, newValue) => {
                        updateLineItem(index, 'imcCode', newValue?.value || '');
                        updateLineItem(index, 'imcCodeDescription', newValue?.label || '');
                        updateLineItem(index, 'imcCodeMondayId', newValue?.mondayId || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="IMC Code *"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: !item.imcCode ? 'error.main' : undefined
                              }
                            }
                          }}
                        />
                      )}
                    />
                    {index > 0 && formData.lineItems[index - 1].imcCode && (
                      <Chip
                        size="small"
                        label={`Same: ${formData.lineItems[index - 1].imcCode}`}
                        onClick={() => {
                          const prev = formData.lineItems[index - 1];
                          updateLineItem(index, 'imcCode', prev.imcCode);
                          updateLineItem(index, 'imcCodeDescription', prev.imcCodeDescription);
                          updateLineItem(index, 'imcCodeMondayId', prev.imcCodeMondayId);
                        }}
                        color="secondary"
                        variant="outlined"
                        sx={{ mt: 0.75, maxWidth: '100%', cursor: 'pointer' }}
                      />
                    )}
                  </Grid>

                  {/* Numbers Row */}
                  <Grid item xs={6} sm={3}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <TextField
                        fullWidth
                        label="Qty *"
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', Number(e.target.value))}
                        inputProps={{ min: 0, step: 1 }}
                      />
                      <IconButton size="small" onClick={() => openCalculator(index, 'quantity')}>
                        <CalculateIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <TextField
                        fullWidth
                        label="Price *"
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => {
                          const val = e.target.value;
                          updateLineItem(index, 'unitPrice', val === '' ? '' : Number(val));
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">€</InputAdornment>
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ minWidth: 140 }}
                      />
                      <IconButton size="small" onClick={() => openCalculator(index, 'unitPrice')}>
                        <CalculateIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label="Company %"
                      value={item.companyPercentage === 100 ? 'All' : item.companyPercentage === 50 ? 'Half' : item.companyPercentage === 33 ? 'Third' : item.companyPercentage === 75 ? 'Three-Quarter' : item.companyPercentage}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === 'All') {
                          updateLineItem(index, 'companyPercentage', 100);
                        } else if (val === 'Half') {
                          updateLineItem(index, 'companyPercentage', 50);
                        } else if (val === 'Third') {
                          updateLineItem(index, 'companyPercentage', 33);
                        } else if (val === 'Three-Quarter') {
                          updateLineItem(index, 'companyPercentage', 75);
                        } else {
                          const num = Number(val);
                          if (!isNaN(num)) {
                            updateLineItem(index, 'companyPercentage', num);
                          }
                        }
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                    />
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                      {[
                        { label: 'All', value: 100 },
                        { label: '3/4', value: 75 },
                        { label: 'Half', value: 50 },
                        { label: '1/3', value: 33 },
                      ].map(({ label, value }) => (
                        <Button
                          key={value}
                          size="small"
                          variant={item.companyPercentage === value ? "contained" : "outlined"}
                          onClick={() => setCompanyPercentage(index, value)}
                          sx={{ minWidth: { xs: 0, sm: 45 }, flex: 1, fontSize: { xs: '0.65rem', sm: '0.7rem' }, px: { xs: 0.5, sm: 1 } }}
                        >
                          {label}
                        </Button>
                      ))}
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label="Total"
                      value={`€${calculateLineItemTotal(item).toFixed(2)}`}
                      disabled
                      InputProps={{ style: { fontWeight: 'bold' } }}
                    />
                  </Grid>

                  {/* Proof Upload */}
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant={item.stagedProof || item.proofAttachment ? "outlined" : "contained"}
                      component="label"
                      startIcon={item.stagedProof || item.proofAttachment ? <CheckIcon /> : <UploadIcon />}
                      color={item.stagedProof || item.proofAttachment ? 'success' : 'primary'}
                      sx={{ py: { xs: 1.5, md: 1 } }}
                    >
                      {item.stagedProof
                        ? item.stagedProof.file.name
                        : item.proofAttachment
                        ? item.proofAttachment.filename
                        : 'Attach Proof Document'}
                      <input
                        type="file"
                        hidden
                        accept="image/*,.pdf"
                        capture="environment"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) stageFile(index, file);
                        }}
                      />
                    </Button>
                  </Grid>
                </Grid>
              </Card>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addLineItem}
              fullWidth
              sx={{ mb: 3 }}
            >
              Add Line Item
            </Button>

            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              multiline
              rows={3}
              sx={{ mb: 3 }}
            />

            {submitStatus.message && (
              <Alert 
                severity={submitStatus.type || 'info'} 
                sx={{ mb: 3 }}
                onClose={() => setSubmitStatus({ type: null, message: null })}
              >
                {submitStatus.message}
              </Alert>
            )}

            {/* Desktop action buttons — hidden on mobile (sticky bar handles it) */}
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ display: { xs: 'none', lg: 'flex' } }}>
              {onCancel && <Button variant="outlined" onClick={onCancel}>Cancel</Button>}
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSaveDraft}
                disabled={uploadProgress.show}
              >
                Save Draft
              </Button>
              <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                onClick={handleSubmitInvoice}
                disabled={uploadProgress.show || submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Invoice'}
              </Button>
            </Stack>
          </Paper>
        </Box>

        {/* Summary — desktop sidebar only */}
        <Paper sx={{ width: 280, p: 2, position: 'sticky', top: 20, height: 'fit-content', display: { xs: 'none', lg: 'block' } }}>
          <Typography variant="h6" gutterBottom>Summary</Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">Items: {formData.lineItems.length}</Typography>
              <Typography variant="body2" color="text.secondary">
                With Proof: {formData.lineItems.filter(i => i.stagedProof || i.proofAttachment).length}
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ pt: 1 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h4">Total:</Typography>
                <Typography variant="h4" color="primary">€{calculateSubtotal().toFixed(2)}</Typography>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Box>

      <Dialog open={calculatorOpen} onClose={() => setCalculatorOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Calculator</DialogTitle>
        <DialogContent>
          <ShowRunnrCalc
            initialValue={calculatorTarget ? formData.lineItems[calculatorTarget.lineIndex][calculatorTarget.field] : 0}
            initialMode="calculator"
            onSubmit={handleCalculatorSubmit}
            onClose={() => setCalculatorOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Mobile sticky bottom bar — hidden on desktop */}
      <Paper
        sx={{
          display: { xs: 'flex', lg: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          borderRadius: 0,
          zIndex: 1200,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.15)',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
            {formData.lineItems.length} item{formData.lineItems.length !== 1 ? 's' : ''}
            {' · '}
            {formData.lineItems.filter(i => i.stagedProof || i.proofAttachment).length} proof{formData.lineItems.filter(i => i.stagedProof || i.proofAttachment).length !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="h6" color="primary" sx={{ lineHeight: 1.3, fontWeight: 700 }}>
            €{calculateSubtotal().toFixed(2)}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={handleSaveDraft}
          disabled={uploadProgress.show}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Save
        </Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
          onClick={handleSubmitInvoice}
          disabled={uploadProgress.show || submitting}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Submit
        </Button>
      </Paper>

      {/* Submission Progress Modal */}
<SubmissionProgressModal
  open={submissionProgress.open}
  steps={submissionProgress.steps}
  currentStep={submissionProgress.currentStep}
  error={submissionProgress.error}
  onClose={() => {
    setSubmissionProgress({
      open: false,
      steps: [],
      currentStep: 0,
      error: null
    });
    if (submissionProgress.error === null) {
      // Navigate to invoice list
      window.location.href = '/admin/invoices';
    }
  }}
/>
    </LocalizationProvider>
  );
};

export default InvoiceForm;